import moment, { Moment, MomentInput } from 'moment-timezone';

import { deprecatedTimeZones } from './deprecatedTimeZones';

export const DEFAULT_TIMEZONE = 'browser';
export const FALLBACK_TIMEZONE = 'UTC';
export const DISPLAY_FORMAT = 'L LTS ZZ';
export const PARSE_FORMAT = 'YYYY-MM-DD HH:mm:ss';

interface TimezoneData {
  name: string;
  offset: string;
}

/**
 * The browser's default timezone.
 *
 * NOTE: It is possible for the returned timezone to be undefined.
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/resolvedOptions
 */
export const timeZone = moment.tz.guess() || FALLBACK_TIMEZONE;

/**
 * The browser's default timezone to display.
 */
export const fmtTimeZone = moment().tz(timeZone)?.format('z');

/**
 * This formats the given timestamp to an ISO string.
 */
export const formatToISOString = (timestamp: string) =>
  moment(timestamp).tz(timeZone).toISOString();

/**
 * This generates a sorted timezone names
 */
export const getTimezones = () =>
  moment.tz
    .names()
    .reduce((tzData, name) => {
      if (!deprecatedTimeZones.includes(name)) {
        tzData.push({
          name,
          offset: moment.tz(name).format('Z'),
        } as TimezoneData);
      }
      return tzData;
    }, [] as TimezoneData[])
    .sort((a, b) => {
      if (a.offset === b.offset) {
        if (a.name === 'UTC') {
          return -1;
        }
        return a.name === b.name ? 0 : a.name > b.name ? 1 : -1;
      }
      const afc = a.offset.charAt(0);
      const bfc = b.offset.charAt(0);
      if (afc === '-') {
        if (bfc === '+') {
          return -1;
        }
        return a.offset > b.offset ? -1 : 1;
      }
      if (bfc === '-') {
        return 1;
      }
      return a.offset > b.offset ? 1 : -1;
    });

/**
 * The sorted Timezone names
 */
export const timeZones = getTimezones();

/**
 * This gets Date object with the default time-zone factored in
 */
export const getDateWithinTimezone = (value: MomentInput) => {
  const momentValue = moment(value);
  const y = momentValue.get('year');
  const m = momentValue.get('month');
  const d = momentValue.get('date');
  const h = momentValue.get('hour');
  const min = momentValue.get('minute');
  const sec = momentValue.get('second');
  const ms = momentValue.get('millisecond');

  return new Date(y, m, d, h, min, sec, ms);
};

/**
 * Given a start and end, get the duration.
 */
export const getDuration = (start: MomentInput, end: MomentInput) =>
  moment.duration(moment(end).diff(start));

/**
 * This parses a DateTime value to display it in UTC time.
 */
export const parseUtcTimestamp = (timestamp?: MomentInput) =>
  moment.utc(timestamp, PARSE_FORMAT);

/**
 * This converts a parsed timestamp to the expected UTC parsable format.
 */
export const convertTimestampToUtc = (timestamp: MomentInput) =>
  parseUtcTimestamp(timestamp).format(PARSE_FORMAT);

/**
 * This converts a string as current timezone timestamp value if possible.
 */
export const parseTimestampString = (value: string, timezone: string) => {
  const time = parseUtcTimestamp(value);
  const isLocal = timezone === DEFAULT_TIMEZONE;

  if (time.isValid()) {
    const timeString = isLocal
      ? time.local().format(DISPLAY_FORMAT)
      : time.tz(timezone).format(DISPLAY_FORMAT);
    const timezoneString = isLocal
      ? fmtTimeZone
      : moment().tz(timezone)?.format('z');

    return `${timeString}${timezoneString ? ` ${timezoneString}` : ''}`;
  }

  return value;
};

// Returns a format function that will always use the specified date format
export const formatDateFactory =
  (dateFormat: string) =>
  (moment: Moment, options?: { year?: boolean; time?: boolean }) => {
    if (!options) {
      return formatShortWithoutTime(moment, dateFormat);
    }

    if (options.year) {
      if (options.time) {
        return formatLongWithTime(moment, dateFormat);
      } else {
        return formatLongWithoutTime(moment, dateFormat);
      }
    }

    if (options.time) {
      return formatShortWithTime(moment, dateFormat);
    }

    return formatShortWithoutTime(moment, dateFormat);
  };

// Looks for the 4-digit year with either a leading or trailing slash
const YEAR_IN_DATE_REGEX = /\/\d{4}|\d{4}\//;

const formatShortWithTime = (moment: Moment, dateFormat: string) =>
  moment.format(`${dateFormat} LT`).replace(YEAR_IN_DATE_REGEX, '');

const formatShortWithoutTime = (moment: Moment, dateFormat: string) =>
  moment.format(dateFormat).replace(YEAR_IN_DATE_REGEX, '');

const formatLongWithTime = (moment: Moment, dateFormat: string) =>
  moment.format(`${dateFormat} LT`);

const formatLongWithoutTime = (moment: Moment, dateFormat: string) =>
  moment.format(dateFormat);

export const generateDate = (offset) =>
  moment().startOf('day').subtract(offset, 'days').toDate();

/**
 * Return the time in milliseconds given a timestamp and timezone.
 * If isLocal=true, then timezone is ignored.
 */
export const getTimeInMilliseconds = (
  timestamp: MomentInput,
  timezone: string,
  isLocal: boolean
) => {
  const parsedTimestamp = parseUtcTimestamp(timestamp);
  return (
    isLocal
      ? parsedTimestamp
      : getDateWithinTimezone(parsedTimestamp.tz(timezone))
  ).valueOf();
};
